import { Link, useLocation } from "react-router";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { cn } from "./ui/utils";

const PRODUCT_MENU_SECTIONS = [
  {
    title: "Garment & Textile",
    links: [
      { to: "/products/textile-home-packaging", label: "Textile & Home Packaging" },
      { to: "/products/garment-packaging", label: "Garment Packaging" },
    ],
  },
  {
    title: "Packaging",
    links: [
      { to: "/products/luxury-packaging", label: "Luxury Packaging" },
      { to: "/products/custom-packaging", label: "Custom Packaging" },
      { to: "/products/business-print-materials", label: "Business Print Materials" },
      { to: "/products/eco-friendly-packaging", label: "Eco-Friendly Packaging" },
    ],
  },
  {
    title: "Retail & Labels",
    links: [
      { to: "/products/retail-display-packaging", label: "Retail & Display Packaging" },
      { to: "/products/labels-stickers", label: "Labels & Stickers" },
    ],
  },
] as const;

const COMPANY_MENU_LINKS = [
  { to: "/about", label: "About Us", description: "Who we are and what drives us." },
  { to: "/sustainability", label: "Sustainability", description: "Materials, certifications & goals." },
  { to: "/facility", label: "Our Facility", description: "Capability, equipment & scale." },
  { to: "/process", label: "Our Process", description: "From brief to delivery, step by step." },
] as const;

/** Desktop mega menu — left: dense grid; right: cream “Explore” column (reference-style). */
const PRODUCT_MEGA_LEFT_LABEL = "Browse by category";
const PRODUCT_MEGA_LEFT_LINKS = [
  { to: "/products/textile-home-packaging", label: "Textile & Home Packaging", description: "Insert cards, wraps & retail-ready packaging for home fabrics." },
  { to: "/products/garment-packaging", label: "Garment Packaging", description: "Labels, tags & branding elements for fashion brands." },
  { to: "/products/luxury-packaging", label: "Luxury Packaging", description: "Premium materials & finishes for cosmetics & perfumes." },
  { to: "/products/custom-packaging", label: "Custom Packaging", description: "Tailored solutions for retail, eCommerce & specialty needs." },
  { to: "/products/business-print-materials", label: "Business Print Materials", description: "Marketing materials & corporate stationery for branding." },
  { to: "/products/eco-friendly-packaging", label: "Eco-Friendly Packaging", description: "FSC, recycled & low‑impact sustainable options." },
  { to: "/products/retail-display-packaging", label: "Retail & Display Packaging", description: "PDQ, shelf‑ready & retail display solutions." },
  { to: "/products/labels-stickers", label: "Labels & Stickers", description: "Barcode labels, price stickers & custom adhesive solutions." },
] as const;

const PRODUCT_MEGA_RIGHT_LABEL = "Explore";
const PRODUCT_MEGA_RIGHT_LINKS = [
  { to: "/products", label: "View all products", description: "Browse the full catalog in one place." },
  { to: "/options", label: "Options library", description: "Materials, finishes & specifications." },
  { to: "/quote", label: "Get an instant quote", description: "Pricing aligned to your brief & volume." },
] as const;


const MEGA_SECTION_LABEL_CLASS =
  "mb-5 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground";

const COMPANY_MEGA_LEFT_LABEL = "Company";
const COMPANY_MEGA_RIGHT_LABEL = "Get started";
const COMPANY_MEGA_RIGHT_LINKS = [
  { to: "/contact", label: "Contact us", description: "Talk through your brief with our team." },
  { to: "/quote", label: "Request a quote", description: "Fast estimates for packaging runs." },
] as const;

const NAV_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/options", label: "Options Library" },
  { to: "/contact", label: "Contact" },
] as const;

const MOBILE_MENU_ANIM_MS = 300;

/**
 * Mega menu: column paints + divider stay fixed; only inner 200% text tracks slide (same spring on both).
 */
const DESKTOP_MEGA_TRACK_SPRING = { type: "spring" as const, stiffness: 380, damping: 36, mass: 0.88 };

/** Dropdown link hover text (no green) */
const DROP_LINK_HOVER = "hover:text-[#0A0A0A]";
/** Dropdown row hover fill — neutral, not `accent` (that tint reads as green) */
const DROP_MENU_HOVER_BG = "hover:bg-black/[0.06]";

function isRouteActive(pathname: string, to: string) {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

function dropMenuRowClass(pathname: string, to: string, layoutClassName: string) {
  const active = isRouteActive(pathname, to);
  return cn(
    layoutClassName,
    "transition-colors",
    DROP_MENU_HOVER_BG,
    active ? "font-semibold text-[#0A0A0A]" : cn("font-medium text-foreground", DROP_LINK_HOVER),
  );
}

type MegaMenuLinkItem = { readonly to: string; readonly label: string; readonly description: string };

function MegaMenuLinkRow({
  pathname,
  item,
  onSelect,
}: {
  pathname: string;
  item: MegaMenuLinkItem;
  onSelect: () => void;
}) {
  const active = isRouteActive(pathname, item.to);
  return (
    <Link
      role="menuitem"
      to={item.to}
      onClick={onSelect}
      className={cn("group block rounded-xl px-2 py-2.5 -mx-2 transition-colors", DROP_MENU_HOVER_BG)}
    >
      <span
        className={cn(
          "block text-sm font-semibold transition-colors",
          active ? "text-[#0A0A0A]" : cn("text-foreground", DROP_LINK_HOVER),
        )}
      >
        {item.label}
      </span>
      <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{item.description}</span>
    </Link>
  );
}

export function Navigation() {
  const location = useLocation();
  const pathname = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [showStickyQuote, setShowStickyQuote] = useState(false);
  /** Past ~1 line of scroll: frosted bar; at top: solid cream */
  const [headerScrolled, setHeaderScrolled] = useState(false);
  /** Desktop: one menu at a time — inner 200% track slides inside the shell (Lovable/Stripe pattern). */
  const [desktopMenu, setDesktopMenu] = useState<null | "company" | "products">(null);
  const [mobileProductExpanded, setMobileProductExpanded] = useState(false);
  const [mobileCompanyExpanded, setMobileCompanyExpanded] = useState(false);
  const desktopNavClusterRef = useRef<HTMLDivElement>(null);
  const desktopMenuLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDesktopMenuTimer = () => {
    if (desktopMenuLeaveTimer.current) {
      clearTimeout(desktopMenuLeaveTimer.current);
      desktopMenuLeaveTimer.current = null;
    }
  };

  const updateDesktopMenu = (next: null | "company" | "products") => {
    clearDesktopMenuTimer();
    setDesktopMenu(next);
  };

  const openDesktopMenu = (which: "company" | "products") => updateDesktopMenu(which);

  const scheduleCloseDesktopMenu = () => {
    clearDesktopMenuTimer();
    desktopMenuLeaveTimer.current = setTimeout(() => setDesktopMenu(null), 200);
  };

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileProductExpanded(false);
    setMobileCompanyExpanded(false);
  };

  useEffect(() => {
    clearDesktopMenuTimer();
    setDesktopMenu(null);
    setMobileProductExpanded(false);
    setMobileCompanyExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      clearDesktopMenuTimer();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setHeaderScrolled(y > 16);
      setShowStickyQuote(y > 600);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setMobileMenuVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setMobileMenuVisible(false);
    const t = setTimeout(() => setMobileMenuMounted(false), MOBILE_MENU_ANIM_MS);
    return () => clearTimeout(t);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuMounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuMounted]);

  useEffect(() => {
    if (!desktopMenu) return;
    const onDown = (e: MouseEvent) => {
      const el = desktopNavClusterRef.current;
      if (el && !el.contains(e.target as Node)) {
        setDesktopMenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [desktopMenu]);

  const productsActive = pathname === "/products" || pathname.startsWith("/products/");

  /** Dropdown parents (Company / Products) stay neutral — green is for direct links & active menu rows only */
  const dropdownTriggerClass = (menuOpen: boolean) =>
    cn(
      "text-sm font-medium transition-colors",
      menuOpen ? "text-[#0A0A0A]" : cn("text-foreground", DROP_LINK_HOVER),
    );

  /**
   * Neutral focus: global `* { outline-ring/50 }` uses `--ring` (green). These triggers must
   * clear that and avoid `ring-accent`, or focus still looks green.
   */
  const isGhostWhitePage = pathname === "/contact" || pathname === "/quote";
  const baseBgColor = isGhostWhitePage ? "#FBFAF7" : "#F6F2EB";

  const dropdownTriggerFocusRing =
    cn("outline-none focus:outline-none focus-visible:outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#0A0A0A]", 
    isGhostWhitePage ? "ring-offset-[#FBFAF7]" : "ring-offset-[#F6F2EB]");

  return (
    <>
      <nav
        className={cn(
          "fixed inset-x-0 top-0 z-50 w-full border-b transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300 ease-out",
          headerScrolled
            ? `border-border/40 bg-[${baseBgColor}]/65 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-150 supports-[backdrop-filter]:bg-[${baseBgColor}]/50`
            : `border-transparent bg-[${baseBgColor}] shadow-none`,
        )}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex min-w-0 items-center">
              <Link
                to="/"
                aria-label="Colour Club Printers — Home"
                className={cn("flex min-w-0 items-center gap-3 rounded-md outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-accent",
                isGhostWhitePage ? "ring-offset-[#FBFAF7]" : "ring-offset-[#F6F2EB]")}
              >
                <img
                  src="/logo.png"
                  alt=""
                  className="h-8 w-auto shrink-0 object-contain object-left sm:h-10"
                  width={140}
                  height={36}
                  decoding="async"
                />
                {/* <div className="flex min-w-0 flex-col justify-center leading-tight">
                  <span className="truncate text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                    Colour Club Printers
                  </span>
                  <span className="truncate text-xs text-muted-foreground sm:text-sm">
                    Paper Printing Services
                  </span>
                </div> */}
              </Link>
            </div>

            <div
              ref={desktopNavClusterRef}
              className="relative hidden items-center gap-x-6 lg:flex xl:gap-x-8"
              onMouseLeave={scheduleCloseDesktopMenu}
            >
              <div className="relative z-[101]" onMouseEnter={() => openDesktopMenu("company")}>
                <button
                  type="button"
                  className={cn(
                    dropdownTriggerClass(desktopMenu === "company"),
                    "inline-flex items-center gap-1 rounded-md",
                    dropdownTriggerFocusRing,
                  )}
                  aria-expanded={desktopMenu === "company"}
                  aria-haspopup="true"
                  aria-controls="nav-company-menu"
                  onClick={() =>
                    updateDesktopMenu(desktopMenu === "company" ? null : "company")
                  }
                >
                  Company
                  <ChevronDown
                    className={cn(
                      "size-4 transition-transform duration-300 ease-out",
                      desktopMenu === "company" && "rotate-180",
                    )}
                    aria-hidden
                  />
                </button>
              </div>

              <div className="relative z-[101]" onMouseEnter={() => openDesktopMenu("products")}>
                <button
                  type="button"
                  className={cn(
                    dropdownTriggerClass(desktopMenu === "products"),
                    "inline-flex items-center gap-1 rounded-md",
                    dropdownTriggerFocusRing,
                  )}
                  aria-expanded={desktopMenu === "products"}
                  aria-haspopup="true"
                  aria-controls="nav-products-menu"
                  onClick={() =>
                    updateDesktopMenu(desktopMenu === "products" ? null : "products")
                  }
                >
                  Products
                  <ChevronDown
                    className={cn(
                      "size-4 transition-transform duration-300 ease-out",
                      desktopMenu === "products" && "rotate-180",
                    )}
                    aria-hidden
                  />
                </button>
              </div>

              {NAV_LINKS.map((item) => {
                const active = isRouteActive(pathname, item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "text-sm transition-colors",
                      active
                        ? "font-semibold text-[#0A0A0A]"
                        : cn("font-medium text-foreground", DROP_LINK_HOVER),
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {/* Lovable/Stripe-style: fixed shell; inner track is 200% wide and translates (x 0 / -50%). */}
              <div className="pointer-events-none absolute left-1/2 top-full z-[100] w-screen min-w-0 -translate-x-1/2">
                <div
                  className="pointer-events-auto h-5 w-full"
                  onMouseEnter={clearDesktopMenuTimer}
                  aria-hidden
                />
                <div className="pointer-events-auto mx-auto w-full max-w-[min(72rem,calc(100vw-1rem))] px-4 sm:px-6 lg:px-8">
                  <AnimatePresence initial={false}>
                    {desktopMenu && (
                      <motion.div
                        key="desktop-dd-open"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.14 }}
                        className="w-full"
                      >
                        <div className={`relative mx-auto w-full max-h-[min(88vh,880px)] max-w-[min(56rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border/80 bg-[${baseBgColor}] shadow-2xl ring-1 ring-black/5`}>
                          {/*
                            Shell is cream so white overlay’s rounded-r-2xl reveals cream in the corner arcs (was invisible on bg-white).
                            w-[56%] = 1.12 / (1.12 + 0.88).
                          */}
                          <div
                            className="pointer-events-none absolute inset-y-0 left-0 z-[1] min-h-[300px] w-[56%] rounded-r-2xl border-r border-black/[0.06] bg-white"
                            aria-hidden
                          />

                          <div className="relative z-[2] flex min-h-[300px] min-w-0 max-h-[min(88vh,880px)]">
                            {/* Left column: text-only track */}
                            <div className="relative min-h-[300px] min-w-0 flex-[1.12] overflow-x-hidden overflow-y-auto">
                              <motion.div
                                className="flex w-[200%] flex-nowrap"
                                initial={false}
                                animate={{
                                  x: desktopMenu === "company" ? "0%" : "-50%",
                                }}
                                transition={DESKTOP_MEGA_TRACK_SPRING}
                              >
                                <div
                                  id="nav-company-menu"
                                  role="menu"
                                  aria-label="Company"
                                  aria-hidden={desktopMenu !== "company"}
                                  className="w-1/2 shrink-0 px-6 py-7 pr-5 sm:px-8 sm:pr-7"
                                >
                                  <p className={MEGA_SECTION_LABEL_CLASS}>{COMPANY_MEGA_LEFT_LABEL}</p>
                                  <div className="space-y-0.5">
                                    {COMPANY_MENU_LINKS.map((item) => (
                                      <MegaMenuLinkRow
                                        key={item.to}
                                        pathname={pathname}
                                        item={item}
                                        onSelect={() => {
                                          clearDesktopMenuTimer();
                                          setDesktopMenu(null);
                                        }}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <div
                                  id="nav-products-menu"
                                  role="menu"
                                  aria-label="Product categories"
                                  aria-hidden={desktopMenu !== "products"}
                                  className="w-1/2 shrink-0 px-6 py-7 pr-5 sm:px-8 sm:pr-7"
                                >
                                  <p className={MEGA_SECTION_LABEL_CLASS}>{PRODUCT_MEGA_LEFT_LABEL}</p>
                                  <div className="grid grid-cols-1 gap-y-0.5 sm:grid-cols-2 sm:gap-x-8">
                                    {PRODUCT_MEGA_LEFT_LINKS.map((item) => (
                                      <MegaMenuLinkRow
                                        key={item.to}
                                        pathname={pathname}
                                        item={item}
                                        onSelect={() => {
                                          clearDesktopMenuTimer();
                                          setDesktopMenu(null);
                                        }}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </motion.div>
                            </div>

                            {/* Right column: text-only track */}
                            <div className="relative min-h-[300px] min-w-0 flex-[0.88] overflow-x-hidden overflow-y-auto">
                              <motion.div
                                className="flex w-[200%] flex-nowrap"
                                initial={false}
                                animate={{
                                  x: desktopMenu === "company" ? "0%" : "-50%",
                                }}
                                transition={DESKTOP_MEGA_TRACK_SPRING}
                              >
                                <div
                                  className="w-1/2 shrink-0 px-5 py-7 sm:px-8"
                                  aria-hidden={desktopMenu !== "company"}
                                >
                                  <p className={MEGA_SECTION_LABEL_CLASS}>{COMPANY_MEGA_RIGHT_LABEL}</p>
                                  <div className="space-y-0.5">
                                    {COMPANY_MEGA_RIGHT_LINKS.map((item) => (
                                      <MegaMenuLinkRow
                                        key={item.to}
                                        pathname={pathname}
                                        item={item}
                                        onSelect={() => {
                                          clearDesktopMenuTimer();
                                          setDesktopMenu(null);
                                        }}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <div
                                  className="w-1/2 shrink-0 px-5 py-7 sm:px-8"
                                  aria-hidden={desktopMenu !== "products"}
                                >
                                  <p className={MEGA_SECTION_LABEL_CLASS}>{PRODUCT_MEGA_RIGHT_LABEL}</p>
                                  <div className="space-y-0.5">
                                    {PRODUCT_MEGA_RIGHT_LINKS.map((item) => (
                                      <MegaMenuLinkRow
                                        key={item.to}
                                        pathname={pathname}
                                        item={item}
                                        onSelect={() => {
                                          clearDesktopMenuTimer();
                                          setDesktopMenu(null);
                                        }}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </motion.div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            <div className="hidden shrink-0 items-center gap-3 lg:flex">
              <Link
                to="/quote"
                className="inline-flex items-center justify-center rounded-lg bg-[#131312] px-6 py-2.5 font-medium text-white transition-colors hover:bg-[#1c1c1b] shadow-sm"
              >
                Get Quote
              </Link>
            </div>

            <button
              type="button"
              className="lg:hidden"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuMounted && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className={cn(
              "fixed inset-0 top-16 z-[55] cursor-default border-0 bg-black/25 transition-opacity duration-300 ease-out lg:hidden",
              mobileMenuVisible ? "opacity-100" : "pointer-events-none opacity-0",
            )}
            onClick={closeMobile}
          />
          <div
            id="mobile-navigation"
            className={cn(
              `fixed left-0 right-0 top-16 z-[60] mx-auto max-h-[calc(100dvh-4rem)] w-full max-w-7xl overflow-y-auto overscroll-contain bg-[${baseBgColor}] transition-[opacity,transform] duration-300 ease-out lg:hidden`,
              mobileMenuVisible
                ? "translate-y-0 opacity-100"
                : "pointer-events-none -translate-y-2 opacity-0",
            )}
          >
            <div className="flex flex-col px-4 py-4 pb-8 sm:px-6">
              <div className="divide-y divide-border/60">
                <div className="py-1">
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between py-3 text-left text-[15px] font-medium first:pt-1 transition-colors",
                      mobileCompanyExpanded ? "text-[#0A0A0A]" : cn("text-foreground", DROP_LINK_HOVER),
                    )}
                    aria-expanded={mobileCompanyExpanded}
                    onClick={() => setMobileCompanyExpanded((o) => !o)}
                  >
                    Company
                    <ChevronDown
                      className={cn(
                        "size-5 shrink-0 transition-transform duration-200",
                        mobileCompanyExpanded && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </button>
                  {mobileCompanyExpanded && (
                    <div className="mt-1.5 mb-3 rounded-2xl bg-black/[0.03] border border-border/40 overflow-hidden shadow-sm">
                      {COMPANY_MENU_LINKS.map((item, i) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={closeMobile}
                          className={cn(
                            "flex items-start gap-3 px-4 py-3.5 transition-colors",
                            i !== 0 && "border-t border-border/40",
                            isRouteActive(pathname, item.to)
                              ? "bg-black/[0.04] font-semibold text-[#0A0A0A]"
                              : cn("text-foreground", DROP_MENU_HOVER_BG, DROP_LINK_HOVER),
                          )}
                        >
                          <div className="min-w-0">
                            <span className="block text-[14px] font-medium">{item.label}</span>
                            <span className="mt-0.5 block text-xs text-muted-foreground">{item.description}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <div className="py-1">
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between py-3 text-left text-[15px] font-medium transition-colors",
                      mobileProductExpanded ? "text-[#0A0A0A]" : cn("text-foreground", DROP_LINK_HOVER),
                    )}
                    aria-expanded={mobileProductExpanded}
                    onClick={() => setMobileProductExpanded((o) => !o)}
                  >
                    Products
                    <ChevronDown
                      className={cn(
                        "size-5 shrink-0 transition-transform duration-200",
                        mobileProductExpanded && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </button>
                  {mobileProductExpanded && (
                    <div className="mt-1.5 mb-3 space-y-2.5">
                      <Link
                        to="/products"
                        onClick={closeMobile}
                        className={cn(
                          "flex items-center justify-between rounded-2xl border border-border/40 bg-black/[0.03] px-4 py-3.5 text-[14px] font-semibold transition-colors shadow-sm",
                          DROP_MENU_HOVER_BG,
                          productsActive ? "text-[#0A0A0A]" : cn("text-foreground", DROP_LINK_HOVER),
                        )}
                      >
                        View all products
                        <ArrowRight className="size-4 shrink-0" aria-hidden />
                      </Link>
                      {PRODUCT_MENU_SECTIONS.map((section) => (
                        <div key={section.title} className="rounded-2xl border border-border/40 bg-black/[0.03] overflow-hidden shadow-sm">
                          <p className="px-4 pt-3 pb-1.5 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                            {section.title}
                          </p>
                          <ul>
                            {section.links.map((item, i) => (
                              <li key={item.to} className={i !== 0 ? "border-t border-border/40" : ""}>
                                <Link
                                  to={item.to}
                                  onClick={closeMobile}
                                  className={cn(
                                    "block px-4 py-3.5 text-[14px] transition-colors",
                                    DROP_MENU_HOVER_BG,
                                    isRouteActive(pathname, item.to)
                                      ? "font-semibold text-[#0A0A0A]"
                                      : cn("font-medium text-foreground", DROP_LINK_HOVER),
                                  )}
                                >
                                  {item.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {NAV_LINKS.map((item) => {
                  const active = isRouteActive(pathname, item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={closeMobile}
                      className={cn(
                        "block py-3.5 text-[15px] transition-colors",
                        active
                          ? "font-semibold text-[#0A0A0A]"
                          : cn("font-medium text-foreground", DROP_LINK_HOVER),
                      )}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>

              <Link
                to="/quote"
                className="mt-4 block rounded-lg bg-[#131312] px-4 py-3.5 text-center text-[15px] font-semibold text-white transition-colors hover:bg-[#1c1c1b] shadow-sm"
                onClick={closeMobile}
              >
                Get Quote
              </Link>
            </div>
          </div>
        </>
      )}

      {showStickyQuote && (
        <div className="fixed bottom-8 right-8 z-50">
          <Link
            to="/quote"
            className="inline-flex items-center justify-center rounded-full bg-[#131312] px-6 py-3 font-medium text-white transition-colors hover:bg-[#1c1c1b] shadow-2xl"
          >
            Get Quote
          </Link>
        </div>
      )}
    </>
  );
}
